import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ImSpinner3 } from 'react-icons/im';
import { AuthContext } from '../../contexts/AuthContext';

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-teal-800 bg-opacity-50 z-50">
    <ImSpinner3 className="animate-spin text-white text-4xl" />
    <p className="mt-4 text-white">Checking access...</p>
  </div>
);

const ProtectedRoute = ({ allowedRoles = [], allowedPermissions = [], children }) => {
  const location = useLocation();
  const { isAuthenticated, authChecked, userRole, permissions } = useContext(AuthContext);

  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  // If token is present, bypass normal authentication checks
  if (token) {
    return children;
  }

  if (!authChecked) return <LoadingSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Case-insensitive role check
  const normalizedUserRole = userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase() : null;
  const isAdmin = normalizedUserRole === 'Admin';

  if (isAdmin) return children;

  const isTokenAccess = normalizedUserRole === 'TokenAccess';

  // Check specific permissions if provided
  if (!isTokenAccess && allowedPermissions.length) {
    const hasPermission = allowedPermissions.some(p => 
      (permissions || []).map(per => per.toLowerCase()).includes(p.toLowerCase())
    );
    if (hasPermission) return children;
  }

  // Fallback to role check
  if (!isTokenAccess && allowedRoles.length) {
    const hasRole = allowedRoles.some(r => r.toLowerCase() === (userRole || '').toLowerCase());
    if (hasRole) return children;
    
    return <Navigate to="/unauthorized" replace />;
  }

  // If permissions were required but none matched
  if (!isTokenAccess && allowedPermissions.length) {
     return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
